import { useState } from "react";
// material
import { Alert, Button, Link, Stack, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import axios from "@utils/axios";
import { SUPPORT_EMAIL, supportMailto } from "@utils/support";
import IdentityFields, { EMPTY_IDENTITY, IdentityValues } from "./IdentityFields";
import { failureMessage } from "./failureMessage";

/**
 * 이메일을 잊은 사람을 위한 화면.
 *
 * 비밀번호를 없앤 뒤로 **이메일이 곧 아이디**라, 잊으면 들어올 길이 없다.
 * 본인인증으로 사람을 확인하고 **그 계정으로 로그인 링크를 보낸다.**
 *
 * 주소를 통째로 보여 주지 않는 이유 — 화면에 띄우면 어깨너머로 읽힌다. «아, 그
 * 주소였지» 를 알아볼 정도만 보여 주고, 실제로 들어오는 것은 메일함에서 한다.
 *
 * ## 입력칸을 직접 그리지 않는다
 *
 * 예전에는 이 화면이 이름·주민번호·통신사·휴대폰번호를 **따로 그렸다.** 그래서
 * `IdentityFields` 를 고쳐도 여기만 옛날 그대로 남았다 — 동의를 붙였을 때 이
 * 화면만 서버에 막힐 뻔했다. 같은 것을 두 번 그리면 반드시 갈라진다.
 */
export default function FindEmailForm({ onBack }: { onBack: () => void }) {
  const [values, setValues] = useState<IdentityValues>(EMPTY_IDENTITY);
  const [txSeqNo, setTxSeqNo] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState("");
  /** 확인이 끝난 뒤의 결과. 빈 배열이면 «못 찾음» 이다. */
  const [found, setFound] = useState<string[] | null>(null);
  /**
   * 찾은 주소로 메일을 **실제로 보냈는가.** 서버가 `linkSent` 로 알려 준다.
   *
   * 예전에는 주소가 하나라도 있으면 «보냈습니다» 라고 했다. 메일 서버가 죽으면 서버는
   * 500 을 냈고(2026-09-23 B1), 고친 뒤로는 찾기는 됐지만 못 보냈다고 알려 준다 —
   * 그때 «보냈습니다» 라고 하면 오지 않을 메일을 기다리게 된다.
   */
  const [linkSent, setLinkSent] = useState(false);

  const send = async (agreedTerms: string[]) => {
    setBusy(true);
    setFailed("");
    try {
      const answer = await axios({
        method: "post", url: "auth/find-email/request",
        data: {
          name: values.name.trim(),
          regFront: values.regFront,
          regRear: values.regRear,
          telComCd: values.telComCd,
          telNo: values.telNo.replace(/\D/g, ""),
          // 서버가 다시 본다. 이유는 `IdentityFields` 의 `onRequestOtp` 주석에.
          agreedTerms,
        },
      });
      setTxSeqNo(answer.data.txSeqNo);
    } catch (error: any) {
      // 서버 문장을 그대로 쓰되 **문자열일 때만**. 422 는 `detail` 이 배열이라 그대로
      // 그리면 React 가 터진다(2026-09-23 B7).
      setFailed(failureMessage(error, {
        fallback: "인증번호를 보내지 못했습니다. 잠시 후 다시 시도해주세요.",
      }));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setFailed("");
    try {
      const answer = await axios({
        method: "post", url: "auth/find-email/confirm",
        data: { txSeqNo, telNo: values.telNo, otpNo: values.otpNo },
      });
      setFound(answer.data.emails ?? []);
      setLinkSent(Boolean(answer.data.linkSent));
    } catch (error: any) {
      // 5xx 를 «인증에 실패했습니다» 로 뭉개지 않는다 — 인증은 성공했는데 그 뒤에서
      // 터졌을 수 있다(B1). `failureMessage` 가 서버 문제 문장을 따로 쓴다.
      setFailed(failureMessage(error, { fallback: "인증에 실패했습니다." }));
    } finally {
      setBusy(false);
    }
  };

  if (found !== null) {
    return (
      <Stack spacing={3}>
        {found.length > 0 && !linkSent && (
          <Alert severity="warning">
            <b>{found.join(", ")}</b> 계정을 찾았지만 로그인 링크 메일을 보내지 못했습니다.
            <br/>
            {/* 이 화면에 온 사람은 주소를 **잊은** 사람이고 보이는 것은 앞 두 글자뿐이다.
                «이 주소로 링크를 받으라» 는 칠 수 없는 주소를 치라는 말이었다(f1-8). */}
            잠시 후 이메일 찾기를 다시 해주세요. 계속 안 되면{" "}
            <Link href={supportMailto("[프리즘 BTS] 로그인 링크 메일 문의")}>{SUPPORT_EMAIL}</Link>
            로 문의해주세요.
            <br/>
            주소가 기억나면 로그인 화면에서 직접 링크를 받을 수도 있습니다.
          </Alert>
        )}
        {found.length > 0 && linkSent && (
          <>
            <Alert severity="success">
              <b>{found.join(", ")}</b> 로 로그인 링크를 보냈습니다.
            </Alert>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              메일의 <b>로그인하기</b> 버튼을 눌러주세요. 링크는 15분 동안 유효합니다.
              <br/>
              메일이 보이지 않으면 스팸함도 확인해주세요.
            </Typography>
          </>
        )}
        {found.length === 0 && (
          /* 본인인증 도입 전에 가입한 분들이 여기 걸린다. 계정에 대조할 값이
             없어 자동으로 찾아 드릴 수 없다. 억지로 이름만으로 맞추면 남의
             주소를 알려 주게 되므로, 사람이 확인하는 길로 안내한다. */
          <Alert severity="info">
            등록된 정보로는 계정을 찾지 못했습니다.
            <br/>
            본인인증을 도입하기 전에 가입하신 분은 자동 조회가 되지 않습니다.
            {/* «고객센터로 문의해주세요» 만 쓰면 **문의할 곳이 없다.** 이메일을
                잊은 사람에게 «연락해 보라» 고만 하는 것은 막다른 길이다. 주소를
                눌러서 바로 메일을 쓸 수 있게 한다. */}
            <br/>
            <Link href={supportMailto("[프리즘 BTS] 계정 이메일 찾기 문의")}>{SUPPORT_EMAIL}</Link>
            로 이름과 연락처를 남겨주시면 확인해 드립니다.
          </Alert>
        )}
        <Button fullWidth size="large" variant="outlined" onClick={onBack}>
          로그인 화면으로
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        본인인증을 하시면 가입하신 이메일로 <b>로그인 링크</b>를 보내드립니다.
      </Typography>

      {failed && <Alert severity="error">{failed}</Alert>}

      <IdentityFields
        values={values}
        onChange={setValues}
        sent={Boolean(txSeqNo)}
        sending={busy && !txSeqNo}
        onRequestOtp={send}
        disabled={busy && Boolean(txSeqNo)}
      />

      {txSeqNo && (
        <LoadingButton
          fullWidth size="large" variant="contained" loading={busy}
          disabled={values.otpNo.length < 4} onClick={confirm}
        >
          확인
        </LoadingButton>
      )}

      <Button fullWidth onClick={onBack}>로그인 화면으로</Button>
    </Stack>
  );
}
